import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Server, 
  Network, 
  Shield, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  Terminal,
  FileText,
  Wifi,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const VPNSetupGuide = () => {
  const [activeTab, setActiveTab] = useState('auto'); // auto, manual, mikrotik, troubleshoot
  const [copiedCommand, setCopiedCommand] = useState('');

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(label);
    toast.success(`Command "${label}" copied!`);
    setTimeout(() => setCopiedCommand(''), 2000);
  };

  const CopyButton = ({ text, label }) => (
    <Button
      size="sm"
      variant="outline"
      onClick={() => copyToClipboard(text, label)}
      className="ml-2"
    >
      {copiedCommand === label ? (
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  const CommandBox = ({ command, label, description }) => (
    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm mb-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          {description && (
            <p className="text-gray-400 text-xs mb-2"># {description}</p>
          )}
          <pre className="whitespace-pre-wrap break-all">{command}</pre>
        </div>
        <CopyButton text={command} label={label} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-100 rounded-lg dark:bg-purple-900/40">
            <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">VPN Setup Guide</h1>
            <p className="text-sm text-gray-600 dark:text-slate-300">L2TP/IPsec VPN untuk Mikrotik tanpa IP Public</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-900/30">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 dark:text-blue-400" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-semibold mb-1">
                Kenapa Perlu VPN?
              </p>
              <p className="text-sm text-blue-800">
                Jika Mikrotik router Anda tidak memiliki IP Public, gunakan VPN tunnel untuk connect ke RADIUS server. 
                VPN ini aman, terenkripsi, dan support multiple routers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'auto', label: 'Auto Setup', icon: Terminal },
            { id: 'manual', label: 'Manual Setup', icon: FileText },
            { id: 'mikrotik', label: 'Mikrotik Config', icon: Wifi },
            { id: 'troubleshoot', label: 'Troubleshooting', icon: AlertCircle }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'auto' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Auto Setup - Quick Install (3 Menit)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
                <p className="text-sm text-green-800 font-semibold mb-2">
                  ✅ Script otomatis akan setup:
                </p>
                <ul className="text-sm text-green-800 space-y-1 ml-4">
                  <li>• Install & configure L2TP server (xl2tpd)</li>
                  <li>• Install & configure IPsec (strongSwan)</li>
                  <li>• Create 3 VPN users (mikrotik1, mikrotik2, mikrotik3)</li>
                  <li>• Configure firewall rules</li>
                  <li>• Enable IP forwarding</li>
                  <li>• Start all services</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">1</span>
                  SSH ke RADIUS Server
                </h3>
                <CommandBox
                  command="ssh kumara@103.179.252.149"
                  label="ssh-radius"
                  description="Connect ke RADIUS VM"
                />
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">2</span>
                  Download & Run Setup Script
                </h3>
                <CommandBox
                  command={`cd /tmp
wget https://raw.githubusercontent.com/your-repo/setup_vpn_radius.sh
chmod +x setup_vpn_radius.sh
sudo ./setup_vpn_radius.sh`}
                  label="run-script"
                  description="Download dan jalankan script auto setup"
                />
                <p className="text-sm text-gray-600 mt-2 dark:text-slate-300">
                  ⏱️ Script akan berjalan selama ±3 menit. Tunggu sampai selesai.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">3</span>
                  VPN Credentials
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm font-semibold text-gray-700 mb-2 dark:text-slate-200">🔐 IPsec PSK:</p>
                  <div className="bg-white p-2 rounded border font-mono text-sm flex items-center justify-between dark:bg-slate-800">
                    <code>StrongIPsecSecret2025!</code>
                    <CopyButton text="StrongIPsecSecret2025!" label="psk" />
                  </div>

                  <p className="text-sm font-semibold text-gray-700 mt-4 mb-2 dark:text-slate-200">👤 VPN Users:</p>
                  <div className="space-y-2">
                    {[
                      { user: 'mikrotik1', pass: 'MikrotikVPN2025!', ip: '10.99.0.10' },
                      { user: 'mikrotik2', pass: 'MikrotikVPN2026!', ip: '10.99.0.11' },
                      { user: 'mikrotik3', pass: 'MikrotikVPN2027!', ip: '10.99.0.12' }
                    ].map((cred) => (
                      <div key={cred.user} className="bg-white p-3 rounded border dark:bg-slate-800">
                        <div className="flex items-center justify-between text-sm">
                          <div className="font-mono">
                            <span className="text-gray-600 dark:text-slate-300">User:</span> <strong>{cred.user}</strong>
                          </div>
                          <Badge variant="secondary" className="text-xs">{cred.ip}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                          <div className="font-mono">
                            <span className="text-gray-600 dark:text-slate-300">Pass:</span> {cred.pass}
                          </div>
                          <CopyButton text={`${cred.user}:${cred.pass}`} label={cred.user} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Catatan:</strong> Setelah setup selesai, lanjut ke tab <strong>"Mikrotik Config"</strong> untuk configure Mikrotik router Anda.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'manual' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Manual Setup - Step by Step</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">1</span>
                  Install Packages
                </h3>
                <CommandBox
                  command="sudo apt update && sudo apt install -y xl2tpd strongswan strongswan-pki"
                  label="install-packages"
                  description="Install L2TP dan IPsec packages"
                />
              </div>

              {/* Step 2 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">2</span>
                  Configure xl2tpd
                </h3>
                <CommandBox
                  command="sudo nano /etc/xl2tpd/xl2tpd.conf"
                  label="edit-xl2tpd"
                  description="Edit konfigurasi L2TP server"
                />
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Paste config ini:</p>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
{`[global]
listen-addr = 0.0.0.0
port = 1701

[lns default]
ip range = 10.99.0.10-10.99.0.250
local ip = 10.99.0.1
require chap = yes
refuse pap = yes
require authentication = yes
name = l2tpserver
pppoptfile = /etc/ppp/options.xl2tpd
length bit = yes`}
                  </pre>
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">3</span>
                  Configure PPP Options
                </h3>
                <CommandBox
                  command="sudo nano /etc/ppp/options.xl2tpd"
                  label="edit-ppp"
                  description="Edit PPP options"
                />
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Paste config ini:</p>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
{`ipcp-accept-local
ipcp-accept-remote
ms-dns 8.8.8.8
ms-dns 8.8.4.4
noccp
auth
crtscts
idle 1800
mtu 1410
mru 1410
nodefaultroute
debug
lock
proxyarp
connect-delay 5000`}
                  </pre>
                </div>
              </div>

              {/* Step 4 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">4</span>
                  Create VPN Users
                </h3>
                <CommandBox
                  command="sudo nano /etc/ppp/chap-secrets"
                  label="edit-users"
                  description="Tambahkan VPN users"
                />
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-2 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Tambahkan di akhir file:</p>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
{`# VPN Users for Mikrotik
mikrotik1    l2tpserver    MikrotikVPN2025!    10.99.0.10
mikrotik2    l2tpserver    MikrotikVPN2026!    10.99.0.11
mikrotik3    l2tpserver    MikrotikVPN2027!    10.99.0.12`}
                  </pre>
                </div>
              </div>

              {/* Step 5 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">5</span>
                  Enable IP Forwarding
                </h3>
                <CommandBox
                  command={`sudo sysctl -w net.ipv4.ip_forward=1
echo "net.ipv4.ip_forward=1" | sudo tee -a /etc/sysctl.conf`}
                  label="ip-forward"
                  description="Enable IP forwarding untuk VPN"
                />
              </div>

              {/* Step 6 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">6</span>
                  Configure Firewall
                </h3>
                <CommandBox
                  command={`sudo ufw allow 1701/udp
sudo ufw allow 500/udp
sudo ufw allow 4500/udp
sudo iptables -t nat -A POSTROUTING -s 10.99.0.0/24 -j MASQUERADE
sudo apt install -y iptables-persistent
sudo netfilter-persistent save`}
                  label="firewall"
                  description="Allow VPN ports dan enable NAT"
                />
              </div>

              {/* Step 7 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">7</span>
                  Start Services
                </h3>
                <CommandBox
                  command={`sudo systemctl restart xl2tpd
sudo systemctl enable xl2tpd
sudo systemctl restart ipsec
sudo systemctl enable ipsec`}
                  label="start-services"
                  description="Start dan enable VPN services"
                />
              </div>

              {/* Step 8 - Verify */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 text-sm dark:bg-green-900/40 dark:text-green-400">✓</span>
                  Verify Setup
                </h3>
                <CommandBox
                  command={`sudo systemctl status xl2tpd
sudo systemctl status ipsec`}
                  label="verify"
                  description="Check services status"
                />
                <p className="text-sm text-gray-600 mt-2 dark:text-slate-300">
                  ✅ Kedua services harus menunjukkan status <strong className="text-green-600 dark:text-green-400">active (running)</strong>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'mikrotik' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Mikrotik L2TP Client Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">1</span>
                  Add L2TP Client Interface
                </h3>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">Via Winbox: PPP → Interface → [+] → L2TP Client</p>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">Via Terminal:</p>
                <CommandBox
                  command={`/interface l2tp-client add \\
  name=vpn-radius \\
  connect-to=103.179.252.149 \\
  user=mikrotik1 \\
  password=MikrotikVPN2025! \\
  profile=default-encryption \\
  add-default-route=no \\
  use-ipsec=yes \\
  ipsec-secret=StrongIPsecSecret2025! \\
  disabled=no`}
                  label="add-l2tp-client"
                  description="Create L2TP client interface"
                />
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3 dark:bg-yellow-900/30">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Penting:</strong> Set <code className="bg-yellow-100 px-1 rounded dark:bg-yellow-900/40">add-default-route=no</code> agar tidak mengganggu routing internet Anda!
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">2</span>
                  Verify VPN Connection
                </h3>
                <CommandBox
                  command="/interface l2tp-client print"
                  label="verify-l2tp"
                  description="Check L2TP client status"
                />
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2 dark:bg-slate-900 dark:border-slate-700">
                  <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Output yang benar:</p>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded">
{`Flags: X - disabled, R - running
 0  R  name="vpn-radius" ... local-address=10.99.0.10 remote-address=10.99.0.1`}
                  </pre>
                  <p className="text-sm text-green-600 mt-2 dark:text-green-400">✅ Status harus <strong>R</strong> (Running)</p>
                </div>
                
                <h4 className="text-sm font-semibold mt-4 mb-2">Test Ping ke RADIUS Server:</h4>
                <CommandBox
                  command="/ping 10.99.0.1 count=5"
                  label="ping-radius"
                  description="Test connectivity via VPN"
                />
              </div>

              {/* Step 3 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">3</span>
                  Add RADIUS Server (via VPN IP)
                </h3>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">Via Winbox: Radius → [+] Add New</p>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">Via Terminal:</p>
                <CommandBox
                  command={`/radius add \\
  address=10.99.0.1 \\
  secret=testing123 \\
  service=ppp,hotspot \\
  timeout=3s`}
                  label="add-radius"
                  description="Add RADIUS server menggunakan VPN gateway IP"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 dark:bg-blue-900/30">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Tips:</strong> Gunakan IP <code className="bg-blue-100 px-1 rounded dark:bg-blue-900/40">10.99.0.1</code> (VPN gateway), bukan 103.179.252.149!
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">4</span>
                  Enable RADIUS for PPPoE
                </h3>
                <CommandBox
                  command="/ppp aaa set use-radius=yes"
                  label="enable-pppoe-radius"
                  description="Enable RADIUS authentication untuk PPPoE"
                />
                <p className="text-sm text-gray-600 mt-2 dark:text-slate-300">Via Winbox: PPP → AAA → ☑ Use RADIUS</p>
              </div>

              {/* Step 5 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-sm dark:bg-indigo-900/40 dark:text-indigo-400">5</span>
                  Enable RADIUS for Hotspot (Optional)
                </h3>
                <CommandBox
                  command="/ip hotspot profile set default use-radius=yes"
                  label="enable-hotspot-radius"
                  description="Enable RADIUS authentication untuk Hotspot"
                />
                <p className="text-sm text-gray-600 mt-2 dark:text-slate-300">Via Winbox: IP → Hotspot → Server Profiles → default → ☑ Use RADIUS</p>
              </div>

              {/* Step 6 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 dark:text-slate-100">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 text-sm dark:bg-green-900/40 dark:text-green-400">✓</span>
                  Test RADIUS Authentication
                </h3>
                <CommandBox
                  command="/radius monitor 0"
                  label="test-radius-auth"
                  description="Monitor RADIUS server status"
                />
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2 dark:bg-green-900/30">
                  <p className="text-sm text-green-800">
                    <strong>✅ Setup Complete!</strong> Mikrotik sekarang siap untuk authenticate customers via RADIUS.
                  </p>
                </div>
              </div>

              {/* Add to Application */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 dark:bg-indigo-900/30">
                <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  Tambahkan Router ke Aplikasi
                </h4>
                <p className="text-sm text-indigo-800 mb-3">
                  Setelah VPN dan RADIUS configured, tambahkan router di aplikasi dengan IP VPN-nya:
                </p>
                <ol className="text-sm text-indigo-800 space-y-1 ml-4">
                  <li>1. Buka <strong>Mikrotik Settings</strong> → Tambah Router</li>
                  <li>2. Host: <code className="bg-indigo-100 px-1 rounded dark:bg-indigo-900/40">10.99.0.10</code> (IP VPN Mikrotik)</li>
                  <li>3. Connection Mode: <strong>Hybrid</strong> atau <strong>RADIUS Only</strong></li>
                  <li>4. RADIUS Secret: <code className="bg-indigo-100 px-1 rounded dark:bg-indigo-900/40">testing123</code></li>
                  <li>5. Save → Router otomatis registered di RADIUS NAS</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'troubleshoot' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                Troubleshooting Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Issue 1 */}
              <div className="border border-gray-200 rounded-lg p-4 dark:border-slate-700">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 dark:text-slate-100">
                  <span className="text-red-600 dark:text-red-400">❌</span>
                  VPN Tidak Connect (Status: Connecting...)
                </h3>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300"><strong>Gejala:</strong> L2TP client terus menunjukkan "connecting" tapi tidak pernah "running"</p>
                
                <div className="bg-gray-50 rounded p-3 space-y-2 dark:bg-slate-900">
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Solusi:</p>
                  
                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">1. Check IPsec service di RADIUS server:</p>
                    <CommandBox
                      command="sudo systemctl status ipsec"
                      label="check-ipsec"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">2. Verify IPsec secret match:</p>
                    <CommandBox
                      command="sudo cat /etc/ipsec.secrets"
                      label="check-ipsec-secret"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">3. Check firewall allows VPN ports:</p>
                    <CommandBox
                      command="sudo ufw status | grep -E '500|4500|1701'"
                      label="check-firewall"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">4. Check logs untuk detail error:</p>
                    <CommandBox
                      command="sudo tail -50 /var/log/auth.log | grep ipsec"
                      label="check-ipsec-logs"
                    />
                  </div>
                </div>
              </div>

              {/* Issue 2 */}
              <div className="border border-gray-200 rounded-lg p-4 dark:border-slate-700">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 dark:text-slate-100">
                  <span className="text-red-600 dark:text-red-400">❌</span>
                  RADIUS Authentication Gagal
                </h3>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300"><strong>Gejala:</strong> PPPoE connection rejected dengan "invalid user or password"</p>
                
                <div className="bg-gray-50 rounded p-3 space-y-2 dark:bg-slate-900">
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Solusi:</p>
                  
                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">1. Verify NAS client terdaftar:</p>
                    <CommandBox
                      command={`sudo mysql -u root -p -e "USE radius; SELECT * FROM nas WHERE nasname='10.99.0.10';"`}
                      label="check-nas-client"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">2. Verify user exists:</p>
                    <CommandBox
                      command={`sudo mysql -u root -p -e "USE radius; SELECT * FROM radcheck WHERE username='testuser';"`}
                      label="check-radius-user"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">3. Check RADIUS secret match:</p>
                    <p className="text-xs text-gray-500 ml-4 dark:text-slate-400">Secret di aplikasi (Mikrotik Settings) harus sama dengan secret di database NAS</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">4. Test RADIUS authentication:</p>
                    <CommandBox
                      command="radtest testuser testpass123 localhost 0 testing123"
                      label="test-radtest"
                    />
                    <p className="text-xs text-green-600 ml-4 mt-1 dark:text-green-400">✅ Harus return "Access-Accept"</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">5. Check FreeRADIUS logs:</p>
                    <CommandBox
                      command="sudo tail -f /var/log/freeradius/radius.log"
                      label="radius-logs"
                    />
                  </div>
                </div>
              </div>

              {/* Issue 3 */}
              <div className="border border-gray-200 rounded-lg p-4 dark:border-slate-700">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 dark:text-slate-100">
                  <span className="text-orange-600 dark:text-orange-400">⚠️</span>
                  VPN Connect Tapi RADIUS Tidak Bisa
                </h3>
                <p className="text-sm text-gray-600 mb-3 dark:text-slate-300"><strong>Gejala:</strong> VPN status "running" tapi RADIUS authentication gagal</p>
                
                <div className="bg-gray-50 rounded p-3 space-y-2 dark:bg-slate-900">
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Solusi:</p>
                  
                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">1. Test ping dari Mikrotik ke RADIUS via VPN:</p>
                    <CommandBox
                      command="/ping 10.99.0.1 count=10"
                      label="ping-from-mikrotik"
                    />
                    <p className="text-xs text-gray-500 ml-4 mt-1 dark:text-slate-400">Harus berhasil dengan reply time &lt;50ms</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">2. Verify RADIUS listening di VPN interface:</p>
                    <CommandBox
                      command="sudo netstat -ulnp | grep 1812"
                      label="check-radius-listen"
                    />
                    <p className="text-xs text-gray-500 ml-4 mt-1 dark:text-slate-400">Harus show listening di 0.0.0.0:1812</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">3. Check routing di RADIUS server:</p>
                    <CommandBox
                      command="ip route | grep 10.99.0"
                      label="check-routing"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-slate-300">4. Pastikan RADIUS address di Mikrotik menggunakan VPN IP:</p>
                    <CommandBox
                      command="/radius print detail"
                      label="check-radius-config"
                    />
                    <p className="text-xs text-orange-600 ml-4 mt-1 dark:text-orange-400">⚠️ Address harus <strong>10.99.0.1</strong>, bukan 103.179.252.149!</p>
                  </div>
                </div>
              </div>

              {/* Monitoring Commands */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                <h4 className="font-semibold text-blue-900 mb-3">🔍 Monitoring Commands</h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-blue-800 mb-1">Check VPN connections:</p>
                    <CommandBox
                      command="sudo tail -f /var/log/syslog | grep xl2tpd"
                      label="monitor-vpn"
                    />
                  </div>
                  
                  <div>
                    <p className="text-sm text-blue-800 mb-1">Check RADIUS authentication:</p>
                    <CommandBox
                      command="sudo tail -f /var/log/freeradius/radius.log"
                      label="monitor-radius"
                    />
                  </div>

                  <div>
                    <p className="text-sm text-blue-800 mb-1">Check active RADIUS sessions:</p>
                    <CommandBox
                      command={`sudo mysql -u root -p -e "USE radius; SELECT username, nasipaddress, acctstarttime FROM radacct WHERE acctstoptime IS NULL;"`}
                      label="active-sessions"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VPNSetupGuide;
